import React, { useRef, useState, useEffect } from "react";
import SignatureCanvas from "react-signature-canvas";
import { Button } from "../buttons";

/**
 * SignaturePad Component
 * Reusable signature canvas with clear functionality
 *
 * @param {Object} props
 * @param {Function} props.onChange - Callback when signature changes (receives data URL)
 * @param {string} props.value - Initial signature data URL
 * @param {string} props.initialValue - Initial signature data URL (only used on mount)
 * @param {boolean} props.readOnly - Whether the signature is read-only
 * @param {number} props.maxWidth - Maximum width of canvas (default: 512)
 * @param {number} props.height - Height of canvas (default: 256)
 */
const SignaturePad = ({
  onChange,
  value,
  initialValue,
  readOnly = false,
  maxWidth = 512,
  height = 256,
}) => {
  const signatureRef = useRef(null);
  const signatureContainerRef = useRef(null);
  const hasLoadedInitialSignature = useRef(false);
  // Initialize to 0 to prevent canvas from expanding container on first render
  const [canvasWidth, setCanvasWidth] = useState(0);
  const [isSigned, setIsSigned] = useState(false);

  // Resize canvas to match container width
  useEffect(() => {
    const handleResize = () => {
      if (signatureContainerRef.current) {
        const containerWidth = signatureContainerRef.current.offsetWidth;
        setCanvasWidth(containerWidth > maxWidth ? maxWidth : containerWidth);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [maxWidth]);

  // Load initial signature if provided (only once, after canvas is ready)
  useEffect(() => {
    if (
      initialValue &&
      !readOnly &&
      signatureRef.current &&
      canvasWidth > 0 &&
      !hasLoadedInitialSignature.current
    ) {
      loadAndScaleSignature(initialValue);
      setIsSigned(true);
      hasLoadedInitialSignature.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue, canvasWidth]);

  // Helper function to load and scale signature to fit current canvas
  const loadAndScaleSignature = (dataURL) => {
    const canvas = signatureRef.current.getCanvas();
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      // Clear canvas first
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Calculate scaling to fit canvas while maintaining aspect ratio
      const scaleX = canvas.width / img.width;
      const scaleY = canvas.height / img.height;
      const scale = Math.min(scaleX, scaleY);

      // Calculate centered position
      const scaledWidth = img.width * scale;
      const scaledHeight = img.height * scale;
      const x = (canvas.width - scaledWidth) / 2;
      const y = (canvas.height - scaledHeight) / 2;

      // Draw scaled and centered image
      ctx.drawImage(img, x, y, scaledWidth, scaledHeight);
    };

    img.src = dataURL;
  };

  const handleClear = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
      setIsSigned(false);
      onChange?.(null);
    }
  };

  const handleSignatureEnd = () => {
    if (signatureRef.current && !signatureRef.current.isEmpty()) {
      setIsSigned(true);
      const dataURL = signatureRef.current.toDataURL();
      onChange?.(dataURL);
    }
  };

  if (readOnly && value) {
    return (
      <div
        className="border border-gray-300 overflow-hidden"
        ref={signatureContainerRef}
      >
        <div className="border-b font-bold border-gray-300 bg-gray-200 p-2 text-center">
          Signature
        </div>
        <div className="flex justify-center items-center bg-white p-4">
          <img
            src={value}
            alt="Signature"
            style={{ maxWidth: "100%", height: "auto" }}
          />
        </div>
      </div>
    );
  }

  if (readOnly && !value) {
    return (
      <div
        className="border border-gray-300 overflow-hidden"
        ref={signatureContainerRef}
      >
        <div className="border-b font-bold border-gray-300 bg-gray-200 p-2 text-center">
          Signature
        </div>
        <div className="flex justify-center items-center bg-white p-8 text-gray-400">
          No signature provided
        </div>
      </div>
    );
  }

  return (
    <div
      className="border border-gray-300 rounded-md overflow-hidden"
      ref={signatureContainerRef}
    >
      <div className="border-b font-bold min-h-[42px] border-gray-300 bg-gray-100 p-2 flex justify-between items-center">
        <span>Signature</span>
        {isSigned && (
          <Button
            variant="custom"
            text="Clear"
            onClick={handleClear}
            className="text-xs px-2 py-1 my-0 bg-red-50 text-red-600 border border-red-300 hover:bg-red-100"
          />
        )}
      </div>

      <div className="flex flex-col items-center bg-gray-200" style={{ minHeight: height }}>
        {canvasWidth > 0 && (
          <SignatureCanvas
            ref={signatureRef}
            options={{
              penColor: "black",
              backgroundColor: "white",
              dotSize: 10,
              minWidth: 0.1,
              maxWidth: 10,
              throttle: 16,
              velocityFilterWeight: 0.7,
            }}
            onEnd={handleSignatureEnd}
            canvasProps={{
              width: canvasWidth > maxWidth ? maxWidth : canvasWidth,
              height: height,
              style: {
                backgroundColor: "white",
                touchAction: "none",
                msTouchAction: "none",
              },
            }}
          />
        )}
      </div>
    </div>
  );
};

export default SignaturePad;
